import { DeleteOutlined, FreeBreakfastOutlined } from "@mui/icons-material";
import { Box } from "@mui/material";
import { type FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { SystemDropdown } from "../../../components/UIToolkit/Inputs/SystemDropDown";
import { SystemValidationTextField } from "../../../components/UIToolkit/Inputs/SystemValidationTextField";
import { EmptyState } from "../../../components/UIToolkit/molecules/EmptyState";
import { SettingsSection } from "../../../components/UIToolkit/molecules/SettingsSection";
import SystemButton from "../../../components/UIToolkit/SystemButton/SystemButton";
import { MOTION, SHADOW } from "../../../theme/tokens";
import { type BreakRuleType, EmploymentType, type PbjBreakRule } from "../../../utils/models";
import { getEmploymentTypeDisplayName } from "../../../utils/utilFunctions";

const EMPLOYMENT_TYPE_OPTIONS = (Object.values(EmploymentType) as EmploymentType[]).map((value) => ({
	value,
	label: getEmploymentTypeDisplayName(value),
	selectedLabel: getEmploymentTypeDisplayName(value),
}));

const ALL_EMPLOYMENT_TYPES = Object.values(EmploymentType) as EmploymentType[];

// A control that stretches to fill its grid track and is allowed to shrink below its
// intrinsic width, so many-chip multi-selects truncate inside the cell instead of overflowing.
const FILL_CONTROL_SX = { width: "100%", minWidth: 0 } as const;

// One-shot "jump": the freshly-added rule drops in from above and bounces to settle, so it's
// clearly the new one. Transform/opacity only (no layout thrash); reduced-motion drops it.
const newRuleJumpSx = {
	"@keyframes pbjNewRuleJump": {
		"0%": { transform: "translateY(-20px)", opacity: 0 },
		"55%": { transform: "translateY(0)", opacity: 1 },
		"73%": { transform: "translateY(-9px)" },
		"88%": { transform: "translateY(0)" },
		"100%": { transform: "translateY(0)" },
	},
	animation: `pbjNewRuleJump ${MOTION.DURATION.HIGHLIGHT_JUMP}ms ${MOTION.EASE_OUT}`,
	"@media (prefers-reduced-motion: reduce)": { animation: "none" },
} as const;

interface PbjBreakRulesTableProps {
	rules: PbjBreakRule[];
	onChange: (next: PbjBreakRule[]) => void;
	/** The department's known HRIS role strings — the source for the Eligible Roles (rawJobs) picker. */
	roleStringOptions: string[];
	/** Id of a just-added rule (rendered first) to spotlight with a one-shot highlight animation. */
	highlightRuleId?: string | null;
}

/**
 * Inline editor for a department's PBJ break rules (the dedicated pbj_break_rules table), rendered as one
 * card per rule. Eligible Roles maps to `rawJobs` — the normalized role-string allowlist matched against
 * pbj_shift.raw_job; empty = all roles. Each card lays its seven controls out in a responsive form grid
 * (label above control), grouped into wrapping rows so nothing overflows horizontally at any width.
 */
export const PbjBreakRulesTable: FC<PbjBreakRulesTableProps> = ({ rules, onChange, roleStringOptions, highlightRuleId }) => {
	const { t } = useTranslation();

	const updateRow = useCallback(
		(index: number, patch: Partial<PbjBreakRule>) => {
			onChange(rules.map((r, i) => (i === index ? { ...r, ...patch } : r)));
		},
		[rules, onChange],
	);

	const removeRow = useCallback(
		(index: number) => {
			onChange(rules.filter((_, i) => i !== index));
		},
		[rules, onChange],
	);

	const typeOptions = [
		{ value: "addition" as BreakRuleType, label: t("pbj_model.breaks.type_addition") },
		{ value: "override" as BreakRuleType, label: t("pbj_model.breaks.type_override") },
	];

	if (rules.length === 0) {
		return (
			<EmptyState icon={<FreeBreakfastOutlined />} title={t("pbj_model.breaks.empty")} hint={t("pbj_model.breaks.empty_hint")} dataTestId="pbj-breaks-empty" />
		);
	}

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%" }} data-testid="pbj-breaks-table">
			{rules.map((row, index) => {
				const key = row.id ?? index;
				return (
					<SettingsSection
						key={key}
						title={row.name?.trim() ? row.name : t("pbj_model.breaks.rule_title", { index: index + 1 })}
						icon={<FreeBreakfastOutlined />}
						layout="stack"
						// The freshly-added rule plays ONLY the spotlight "jump" — suppress the section's own
						// mount fade so the two entrances don't stack on top of each other.
						disableEntrance={row.id === highlightRuleId}
						sx={row.id === highlightRuleId ? [{ boxShadow: SHADOW.PANEL_SOFT }, newRuleJumpSx] : { boxShadow: SHADOW.PANEL_SOFT }}
						action={
							<SystemButton
								buttonVariant="borderless"
								size="small"
								leadingIcon={<DeleteOutlined sx={{ fontSize: 20 }} />}
								onClick={() => removeRow(index)}
								tooltip={t("pbj_model.breaks.delete")}
								noFullWidthMobile
								sx={(theme) => ({ color: theme.palette.text.secondary, "&:hover": { color: theme.palette.error.main } })}
								data-testid={`pbj-breaks-delete-${index}`}
							/>
						}
						dataTestId={`pbj-breaks-rule-${index}`}
					>
						{/* Row 1 — Name (wide) + Type (narrow) */}
						<Box sx={{ display: "grid", gap: 2, alignItems: "start", gridTemplateColumns: { xs: "1fr", sm: "minmax(0, 1.8fr) minmax(0, 1fr)" } }}>
							<SystemValidationTextField
								fullWidth
								reserveHeightForHelperText={false}
								label={t("pbj_model.breaks.column.name")}
								value={row.name}
								onChange={(e) => updateRow(index, { name: e.target.value })}
								formControlSx={FILL_CONTROL_SX}
								dataTestId={`pbj-breaks-name-${index}`}
							/>
							<SystemDropdown<BreakRuleType>
								fullWidth
								label={t("pbj_model.breaks.column.type")}
								formControlSx={FILL_CONTROL_SX}
								items={typeOptions}
								selectedItem={row.type ?? "addition"}
								valueToString={(v) => String(v)}
								onChange={(value) => updateRow(index, { type: (value ?? "addition") as BreakRuleType })}
								data-testid={`pbj-breaks-type-${index}`}
							/>
						</Box>

						{/* Row 2 — the two multi-selects, equal width (empty = all, kept as the placeholder hint) */}
						<Box sx={{ display: "grid", gap: 2, alignItems: "start", gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" } }}>
							<SystemDropdown<string>
								fullWidth
								multiple
								showFilter
								label={t("pbj_model.breaks.column.eligible_roles")}
								placeholder={t("pbj_model.breaks.all_roles")}
								formControlSx={FILL_CONTROL_SX}
								items={roleStringOptions.map((role) => ({ value: role, label: role, selectedLabel: role }))}
								selectedItems={row.rawJobs ?? []}
								valueToString={(v) => String(v)}
								onChangeMultiple={(selectedValues) => {
									const valid = new Set(roleStringOptions);
									const roles = (selectedValues as string[]).filter((v) => valid.has(v));
									// [] = "all roles" (no filter); a full selection collapses back to [].
									updateRow(index, { rawJobs: roles.length === roleStringOptions.length ? [] : roles });
								}}
								data-testid={`pbj-breaks-roles-${index}`}
							/>
							<SystemDropdown<EmploymentType>
								fullWidth
								multiple
								label={t("pbj_model.breaks.column.employment_type")}
								placeholder={t("pbj_model.breaks.all_employment_types")}
								formControlSx={FILL_CONTROL_SX}
								items={EMPLOYMENT_TYPE_OPTIONS}
								selectedItems={(row.employmentTypes ?? []) as EmploymentType[]}
								valueToString={(v) => String(v)}
								onChangeMultiple={(selectedValues) => {
									const types = (selectedValues as string[]).filter((v) => ALL_EMPLOYMENT_TYPES.includes(v as EmploymentType)) as EmploymentType[];
									updateRow(index, {
										employmentTypes: types.length === EMPLOYMENT_TYPE_OPTIONS.length ? ALL_EMPLOYMENT_TYPES : types,
									});
								}}
								data-testid={`pbj-breaks-employment-${index}`}
							/>
						</Box>

						{/* Row 3 — three compact numeric fields */}
						<Box sx={{ display: "grid", gap: 2, alignItems: "start", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" } }}>
							<SystemValidationTextField
								fullWidth
								reserveHeightForHelperText={false}
								inputType="number"
								label={t("pbj_model.breaks.column.break_limit")}
								value={row.breakLimit == null ? "" : String(row.breakLimit)}
								onChange={(e) => updateRow(index, { breakLimit: e.target.value === "" ? 0 : Number(e.target.value) })}
								injectedProps={{ slotProps: { htmlInput: { min: 0, step: 0.5 } } }}
								formControlSx={FILL_CONTROL_SX}
								dataTestId={`pbj-breaks-limit-${index}`}
							/>
							<SystemValidationTextField
								fullWidth
								reserveHeightForHelperText={false}
								inputType="number"
								label={t("pbj_model.breaks.column.break_start")}
								value={row.breakStart == null ? "" : String(row.breakStart)}
								onChange={(e) => updateRow(index, { breakStart: e.target.value === "" ? undefined : Number(e.target.value) })}
								placeholder={t("pbj_model.breaks.break_start_placeholder")}
								injectedProps={{ slotProps: { htmlInput: { min: 0, step: 0.5 } } }}
								formControlSx={FILL_CONTROL_SX}
								dataTestId={`pbj-breaks-start-${index}`}
							/>
							<SystemValidationTextField
								fullWidth
								reserveHeightForHelperText={false}
								inputType="number"
								label={t("pbj_model.breaks.column.break_time")}
								value={row.breakTime == null ? "" : String(row.breakTime)}
								onChange={(e) => updateRow(index, { breakTime: e.target.value === "" ? 0 : Number(e.target.value) })}
								injectedProps={{ slotProps: { htmlInput: { min: 1, step: 1 } } }}
								formControlSx={FILL_CONTROL_SX}
								dataTestId={`pbj-breaks-time-${index}`}
							/>
						</Box>
					</SettingsSection>
				);
			})}
		</Box>
	);
};
